if (!customElements.get('product-form')) {
  const UK_VAT_MULTIPLIER = window.PMUKRetailPricing?.UK_VAT_MULTIPLIER || 1.2;
  const METERS_TO_YARDS_MULTIPLIER = 1.0936133;
  const YARD_EQUIVALENT_DECIMAL_PLACES = 3;
  const getUKRetailPrice =
    window.PMUKRetailPricing?.getUKRetailPrice ||
    ((basePrice) => {
      const parsedBasePrice = Number.parseInt(basePrice, 10);

      if (!Number.isFinite(parsedBasePrice) || parsedBasePrice < 0) {
        return 0;
      }

      return Math.round(parsedBasePrice * UK_VAT_MULTIPLIER);
    });

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionValueSelectionUnsubscriber = undefined;
      quantityUpdateUnsubscriber = undefined;

      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;
        this.container = this.closest('product-info') || document;
        const variantIdInput = this.variantIdInput;
        if (variantIdInput) variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span');
        this.productVendor = this.dataset.productVendor;
        this.fabricsVendorName = this.dataset.fabricsVendorName?.trim() || '';
        this.wallpaperVendorName = this.dataset.wallpaperVendorName?.trim() || '';
        this.quickOrderList = this.container.querySelector('quick-order-list');
        this.handleQuickOrderModeChange = this.handleQuickOrderModeChange.bind(this);
        this.handleQuickOrderQuantitiesReset = this.handleQuickOrderQuantitiesReset.bind(this);
        this.allocationMessageContainer = this.querySelector('[data-allocation-messages]');
        this.allocationMessageRolls = this.querySelector('[data-allocation-message="rolls"]');
        this.allocationMessageBatches = this.querySelector('[data-allocation-message="batches"]');
        this.totalPriceValue = this.querySelector('[data-product-total-value]');
        this.moneyFormat = this.dataset.moneyFormat || window?.Shopify?.money_format;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.quickOrderList) {
          this.container.addEventListener('quick-order-mode-change', this.handleQuickOrderModeChange);
          this.container.addEventListener('quick-order-quantities-reset', this.handleQuickOrderQuantitiesReset);
        }

        this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this);
        this.refreshQuantityElements();

        if (this.quantityInput) {
          queueMicrotask(() => {
            if (!this.isConnected) return;
            this.applySingleQuantityAllocation();
          });
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || event.data.sectionId !== this.dataset.sectionId) return;
          this.renderPdpPricing(event.data.variant);
        });

        this.optionValueSelectionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          const target = event?.data?.target;
          if (target && !this.container.contains(target)) return;
          this.renderQuantityStockAvailability(this.findVariantBySelectedOptions());
        });

        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.renderVendorSubtotal();
        });

        this.renderPdpPricing(this.getSelectedVariant());
      }

      onSubmitHandler(evt) {
        if (!this.submitButton) return;
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.sanitizeQuantityInputValue();

        // const quickOrderList = document.querySelector('quick-order-list');

        // If the quick order list exists, delegate to it
        // if (quickOrderList && typeof quickOrderList.getUpdatesAndSend === 'function') {
          // const success = quickOrderList.getUpdatesAndSend();
        if (this.shouldUseManualQuickOrderList()) {
          const success = this.quickOrderList.getUpdatesAndSend();
          if (!success) {
            // validation failed, stop execution and reset button
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.submitButtonSpinner?.classList.add('hidden');
          }
          return; // stop single-item flow
        }

        if (this.shouldAutoAllocate()) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.submitButtonSpinner?.classList.remove('hidden');

          const requestedQuantity = parseInt(this.quantityInput?.value, 10) || 0;
          const allocation = this.buildAllocation(requestedQuantity, this.getCurrentAllocationsIfManual());

          if (!allocation.success) {
            this.handleErrorMessage(allocation.errorMessage);
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButtonSpinner?.classList.add('hidden');
            return;
          }

          this.applyAllocationToTable(allocation.allocations, allocation.messages);
          this.addAllocatedItemsToCart(allocation.allocations);
          return;
        }

        // fallback to standard single-item add-to-cart
        this.addSingleItemToCart(evt);
      }

      handleQuantityInputChange() {
        this.renderVendorSubtotal();

        if (!this.shouldAutoAllocate()) return;

        const requestedQuantity = parseInt(this.quantityInput?.value, 10) || 0;
        const currentAllocations = this.getCurrentAllocationsIfManual();

        if (!requestedQuantity) {
          if (typeof this.quickOrderList?.applyAllocation === 'function') {
            this.quickOrderList.applyAllocation({}, '');
          }
          this.updateAllocationMessages([]);
          return;
        }

        const allocation = this.buildAllocation(requestedQuantity, currentAllocations);

        if (!allocation.success) {
          if (typeof this.quickOrderList?.applyAllocation === 'function') {
            this.quickOrderList.applyAllocation({}, '');
          }
          this.updateAllocationMessages([]);
          return;
        }

        this.applyAllocationToTable(allocation.allocations, allocation.messages);
      }

      disconnectedCallback() {
        this.unbindQuantityInputEvents();
        this.variantChangeUnsubscriber?.();
        this.optionValueSelectionUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
      }

      getQuantityInputElement() {
        return (
          this.container.querySelector('[data-single-quantity="true"]') ||
          this.form.querySelector('input[name="quantity"]') ||
          this.container.querySelector(`input[name="quantity"][form="${this.form?.id}"]`)
        );
      }

      bindQuantityInputEvents() {
        if (!this.quantityInput) return;

        this.quantityInput.addEventListener('input', this.handleQuantityInputChange);
        this.quantityInput.addEventListener('change', this.handleQuantityInputChange);
      }

      unbindQuantityInputEvents() {
        if (!this.quantityInput) return;

        this.quantityInput.removeEventListener('input', this.handleQuantityInputChange);
        this.quantityInput.removeEventListener('change', this.handleQuantityInputChange);
      }

      refreshQuantityElements() {
        const nextQuantityInput = this.getQuantityInputElement();

        if (this.quantityInput !== nextQuantityInput) {
          this.unbindQuantityInputEvents();
          this.quantityInput = nextQuantityInput;
          this.bindQuantityInputEvents();
        }

        this.quantityYardEquivalent = this.container.querySelector('[data-quantity-yard-equivalent]');
        this.quantityYardEquivalentText = this.container.querySelector('[data-quantity-yard-equivalent-text]');
        this.quantityStockAvailability = this.container.querySelector('[data-quantity-stock-availability]');
        this.quantityStockAvailabilityText = this.container.querySelector('[data-quantity-stock-availability-text]');
      }

      handleQuickOrderModeChange(event) {
        this.refreshQuantityElements();

        const { mode, quantity } = event.detail;
        const useTable = mode === 'table';

        this.toggleQuantityInput(useTable);

        if (useTable) {
          this.updateAllocationMessages(this.getManualSelectionMessages());
        } else if ((parseInt(quantity, 10) || 0) === 0) {
          this.updateAllocationMessages([]);
        }

        if (typeof quantity === 'number' && this.quantityInput) {
          this.quantityInput.value = quantity;
          this.quantityInput.setAttribute('value', quantity);
        }

        this.renderVendorSubtotal();
      }

      handleQuickOrderQuantitiesReset() {
        requestAnimationFrame(() => this.applySingleQuantityAllocation());
      }

      toggleQuantityInput(disable) {
        this.refreshQuantityElements();
        if (!this.quantityInput) return;

        if (disable) {
          this.quantityInput.setAttribute('disabled', 'disabled');
        } else {
          this.quantityInput.removeAttribute('disabled');
        }
      }

      shouldUseManualQuickOrderList() {
        return this.quickOrderList && this.quickOrderList.isManualMode && typeof this.quickOrderList.getUpdatesAndSend === 'function';
      }

      shouldAutoAllocate() {
        return this.productVendor === this.fabricsVendorName && this.quickOrderList && this.quantityInput;
      }

      isFabricsVendorProduct() {
        return this.productVendor === this.fabricsVendorName;
      }

      isWallpaperVendorProduct() {
        return this.productVendor === this.wallpaperVendorName;
      }

      getSelectedVariant() {
        const selectedVariantId = this.getSelectedVariantId();
        const variantFromId = this.findVariantById(selectedVariantId);
        const selectedVariant = this.parseJsonScript('variant-selects [data-selected-variant]');
        const matchedVariant = this.findVariantBySelectedOptions();
        const fallbackVariant = this.parseJsonScript('[data-product-form-selected-variant]');

        if (variantFromId) return variantFromId;
        if (!selectedVariant) return this.withVariantInventory(matchedVariant || fallbackVariant);
        if (!matchedVariant) return this.withVariantInventory(selectedVariant);

        return this.withVariantInventory(selectedVariant.id === matchedVariant.id ? selectedVariant : matchedVariant);
      }

      parseJsonScript(selector) {
        const json = this.container?.querySelector(selector)?.textContent?.trim();
        if (!json) return null;

        try {
          return JSON.parse(json);
        } catch (error) {
          console.error(`Failed to parse ${selector}`, error);
          return null;
        }
      }

      getVariantList() {
        const variants = this.parseJsonScript('variant-selects [data-variant-list]');
        return Array.isArray(variants) ? variants.map((variant) => this.withVariantInventory(variant)) : [];
      }

      getVariantInventoryList() {
        const variantInventoryList = this.parseJsonScript('variant-selects [data-variant-inventory-list]');
        if (Array.isArray(variantInventoryList)) return variantInventoryList;

        const selectedVariantInventory = this.parseJsonScript('[data-product-form-selected-variant-inventory]');
        return selectedVariantInventory ? [selectedVariantInventory] : [];
      }

      getVariantInventoryById(variantId) {
        if (!variantId) return null;

        return (
          this.getVariantInventoryList().find((variantInventory) => `${variantInventory?.id}` === `${variantId}`) ||
          null
        );
      }

      withVariantInventory(variant) {
        if (!variant) return null;

        const variantInventory = this.getVariantInventoryById(variant.id);
        return variantInventory ? { ...variant, ...variantInventory } : variant;
      }

      getSelectedOptionValues() {
        const variantSelects = this.container?.querySelector('variant-selects');
        if (!variantSelects) return [];

        return Array.from(variantSelects.querySelectorAll('select, fieldset input:checked')).map((input) =>
          input.value?.trim()
        );
      }

      findVariantBySelectedOptions() {
        const selectedOptions = this.getSelectedOptionValues();
        if (!selectedOptions.length) return null;

        return (
          this.getVariantList().find((variant) => {
            const variantOptions = Array.isArray(variant.options)
              ? variant.options
              : [variant.option1, variant.option2, variant.option3].filter(
                  (value) => value !== null && value !== undefined
                );

            if (variantOptions.length !== selectedOptions.length) return false;

            return variantOptions.every((value, index) => value === selectedOptions[index]);
          }) || null
        );
      }

      getSelectedVariantId() {
        const variantId = this.variantIdInput?.value?.trim();
        return variantId || '';
      }

      findVariantById(variantId) {
        if (!variantId) return null;

        return this.getVariantList().find((variant) => `${variant?.id}` === variantId) || null;
      }

      renderPdpPricing(variant = this.getSelectedVariant()) {
        variant = this.withVariantInventory(variant);
        this.dataset.selectedVariantId = variant?.id ? `${variant.id}` : '';
        if (this.totalPriceValue) {
          this.totalPriceValue.dataset.variantId = this.dataset.selectedVariantId;
        }

        if (!variant) {
          this.renderQuantityStockAvailability(null);
          this.clearSubtotal();
          return;
        }

        this.syncUnitPriceFromVariant(variant);
      }

      syncUnitPriceFromVariant(variant) {
        const variantPrice = parseInt(variant?.price, 10);

        if (Number.isNaN(variantPrice)) {
          this.clearSubtotal();
          return;
        }

        this.setUnitPrice(variantPrice);
      }

      getUnitPrice() {
        const storedPrice = parseInt(this.totalPriceValue?.dataset.unitPrice || this.dataset.initialPrice || 0, 10);
        return Number.isNaN(storedPrice) ? 0 : storedPrice;
      }

      setUnitPrice(price) {
        const parsedPrice = parseInt(price, 10);
        if (Number.isNaN(parsedPrice) || parsedPrice < 0) return;

        const priceMultiplier = Number.parseFloat(this.dataset.priceMultiplier || '1');
        const normalizedMultiplier = Number.isNaN(priceMultiplier) ? 1 : priceMultiplier;
        const adjustedPrice =
          normalizedMultiplier === UK_VAT_MULTIPLIER
            ? getUKRetailPrice(parsedPrice)
            : Math.round(parsedPrice * normalizedMultiplier);

        this.dataset.initialPrice = adjustedPrice;
        if (this.totalPriceValue) {
          this.totalPriceValue.dataset.unitPrice = adjustedPrice;
        }

        this.renderVendorSubtotal();
      }

      formatMoney(amount, formatString) {
        const cleanedFormat = typeof formatString === 'string' && formatString.trim() !== '' ? formatString : '${{amount}}';
        const placeholderRegex = /\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator|amount_no_decimals_with_space_separator|amount_with_apostrophe_separator)\s*\}\}/;
        const placeholder = cleanedFormat.match(placeholderRegex)?.[1] || 'amount';

        const formatWithDelimiters = (cents, precision, thousands, decimal) => {
          const number = (cents / 100).toFixed(Math.max(0, precision));
          const [integerPart, fractionalPart] = number.split('.');
          const withThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fractionalPart ? `${withThousands}${decimal}${fractionalPart}` : withThousands;
        };

        const formatters = {
          amount: () => formatWithDelimiters(amount, 2, ',', '.'),
          amount_no_decimals: () => formatWithDelimiters(amount, 0, ',', '.'),
          amount_with_comma_separator: () => formatWithDelimiters(amount, 2, '.', ','),
          amount_no_decimals_with_comma_separator: () => formatWithDelimiters(amount, 0, '.', ','),
          amount_no_decimals_with_space_separator: () => formatWithDelimiters(amount, 0, ' ', ','),
          amount_with_apostrophe_separator: () => formatWithDelimiters(amount, 2, "'", '.'),
        };

        const formatter = formatters[placeholder] || formatters.amount;
        const value = formatter();

        return cleanedFormat.replace(placeholderRegex, value);
      }

      clearSubtotal() {
        if (!this.totalPriceValue) return;

        this.dataset.initialPrice = '';
        this.totalPriceValue.textContent = '';
        this.totalPriceValue.dataset.variantId = '';
        delete this.totalPriceValue.dataset.unitPrice;
      }

      getCurrentQuantityValue() {
        this.refreshQuantityElements();
        return parseInt(this.quantityInput?.value, 10) || 0;
      }

      getVisibleQuantityValue() {
        this.refreshQuantityElements();
        if (!this.quantityInput) return 0;

        const parsedValue = parseInt(this.quantityInput.value, 10);
        if (Number.isNaN(parsedValue)) return 0;

        const minValue = this.quantityInput.min ? parseInt(this.quantityInput.min, 10) : null;
        const inputMaxValue = this.quantityInput.max ? parseInt(this.quantityInput.max, 10) : null;
        const dataMaxValue = this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null;
        const cartQuantity = this.quantityInput.dataset.cartQuantity
          ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
          : 0;
        const computedDataMax =
          dataMaxValue !== null && !Number.isNaN(dataMaxValue)
            ? Math.max(0, dataMaxValue - (Number.isNaN(cartQuantity) ? 0 : cartQuantity))
            : null;
        const maxValue = inputMaxValue !== null && !Number.isNaN(inputMaxValue) ? inputMaxValue : computedDataMax;

        let normalizedValue = Math.max(0, parsedValue);

        if (minValue !== null && !Number.isNaN(minValue)) {
          normalizedValue = Math.max(normalizedValue, minValue);
        }

        if (maxValue !== null && !Number.isNaN(maxValue)) {
          normalizedValue = Math.min(normalizedValue, maxValue);
        }

        return normalizedValue;
      }

      getManualQuickOrderQuantity() {
        if (typeof this.quickOrderList?.getRequestedQuantity === 'function') {
          const requestedQuantity = parseInt(this.quickOrderList.getRequestedQuantity(), 10);
          return Number.isNaN(requestedQuantity) ? 0 : Math.max(0, requestedQuantity);
        }

        return this.getVisibleQuantityValue();
      }

      getAllocatedProductQuantity(requestedQuantity) {
        const allocation = this.buildAllocation(requestedQuantity, this.getCurrentAllocationsIfManual());

        if (!allocation.success) {
          return 0;
        }

        return allocation.allocations.reduce((total, item) => total + (parseInt(item.quantity, 10) || 0), 0);
      }

      getEffectiveSubtotalQuantity(selectedVariant = this.getSelectedVariant()) {
        if (!selectedVariant) return 0;

        if (this.isFabricsVendorProduct()) {
          if (this.shouldUseManualQuickOrderList()) {
            return this.getManualQuickOrderQuantity();
          }

          if (this.shouldAutoAllocate()) {
            return this.getAllocatedProductQuantity(this.getVisibleQuantityValue());
          }
        }

        if (this.isWallpaperVendorProduct()) {
          return this.getVisibleQuantityValue();
        }

        return this.getCurrentQuantityValue();
      }

      getQuantityYardEquivalentMetersPerUnit() {
        this.refreshQuantityElements();

        const metersPerUnit = Number.parseFloat(this.quantityYardEquivalent?.dataset?.yardMetersPerUnit || '0');

        if (!Number.isFinite(metersPerUnit) || metersPerUnit <= 0) {
          return 0;
        }

        return metersPerUnit;
      }

      formatYardEquivalent(quantity) {
        const parsedQuantity = Number.parseFloat(quantity);
        const metersPerUnit = this.getQuantityYardEquivalentMetersPerUnit();

        if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0 || metersPerUnit <= 0) {
          return '';
        }

        return (parsedQuantity * metersPerUnit * METERS_TO_YARDS_MULTIPLIER).toFixed(YARD_EQUIVALENT_DECIMAL_PLACES);
      }

      renderQuantityYardEquivalent() {
        this.refreshQuantityElements();
        if (!this.quantityYardEquivalent || !this.quantityYardEquivalentText) return;

        const yardEquivalent = this.formatYardEquivalent(this.quantityInput?.value);

        if (!yardEquivalent) {
          this.quantityYardEquivalent.hidden = true;
          return;
        }

        const yardTemplate = this.quantityYardEquivalent.dataset.yardTemplate || '__YARDS__';
        this.quantityYardEquivalentText.textContent = yardTemplate.replace('__YARDS__', yardEquivalent);
        this.quantityYardEquivalent.hidden = false;
      }

      getQuantityStockAvailabilityText(variant) {
        if (variant?.inventory_policy === 'continue') {
          return '';
        }

        const inventoryQuantity = Number.parseInt(variant?.inventory_quantity, 10);

        if (!Number.isFinite(inventoryQuantity)) {
          return '';
        }

        const availableQuantity = Math.max(0, inventoryQuantity);
        if (availableQuantity > 10) {
          return this.quantityStockAvailability?.dataset?.inStockTemplate || '';
        }

        const availableTemplate = this.quantityStockAvailability?.dataset?.availableTemplate || '__QUANTITY__';
        return availableTemplate.replace('__QUANTITY__', availableQuantity);
      }

      renderQuantityStockAvailability(variant = this.getSelectedVariant()) {
        this.refreshQuantityElements();
        if (!this.quantityStockAvailability || !this.quantityStockAvailabilityText) return;

        const stockAvailabilityText = this.getQuantityStockAvailabilityText(variant);
        if (!stockAvailabilityText) {
          this.quantityStockAvailability.hidden = true;
          return;
        }

        this.quantityStockAvailabilityText.textContent = stockAvailabilityText;
        this.quantityStockAvailability.hidden = false;
      }

      renderVendorSubtotal(selectedVariant = this.getSelectedVariant()) {
        this.refreshQuantityElements();
        this.renderQuantityYardEquivalent();
        this.renderQuantityStockAvailability(selectedVariant);

        if (!this.totalPriceValue) return;

        if (!selectedVariant) {
          this.clearSubtotal();
          return;
        }

        const quantity = this.getEffectiveSubtotalQuantity(selectedVariant);
        const totalPrice = this.getUnitPrice() * quantity;
        const format = this.moneyFormat || window.Shopify?.money_format;

        if (window.PMLocalizedMoney?.formatElement) {
          this.totalPriceValue.dataset.currencyCode =
            this.totalPriceValue.dataset.currencyCode || this.dataset.localizationCurrency || '';
          this.totalPriceValue.dataset.countryCode =
            this.totalPriceValue.dataset.countryCode || this.dataset.localizationCountry || '';
          this.totalPriceValue.dataset.languageCode =
            this.totalPriceValue.dataset.languageCode || this.dataset.localizationLanguage || document.documentElement.lang || '';
          this.totalPriceValue.dataset.moneyWithCurrency = 'false';
          this.totalPriceValue.dataset.fallback = this.totalPriceValue.dataset.fallback || this.totalPriceValue.textContent || '';
          window.PMLocalizedMoney.formatElement(this.totalPriceValue, totalPrice);
          return;
        }

        if (typeof Shopify?.formatMoney === 'function') {
          this.totalPriceValue.textContent = Shopify.formatMoney(totalPrice, format);
        } else {
          this.totalPriceValue.textContent = this.formatMoney(totalPrice, format);
        }
      }

      sanitizeQuantityInputValue() {
        this.refreshQuantityElements();
        if (!this.quantityInput) return null;

        const parsedValue = parseInt(this.quantityInput.value, 10);
        if (Number.isNaN(parsedValue)) return null;

        const minValue = this.quantityInput.min ? parseInt(this.quantityInput.min, 10) : null;
        const inputMaxValue = this.quantityInput.max ? parseInt(this.quantityInput.max, 10) : null;
        const dataMaxValue = this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null;
        const cartQuantity = this.quantityInput.dataset.cartQuantity
          ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
          : 0;
        const computedDataMax =
          dataMaxValue !== null && !Number.isNaN(dataMaxValue)
            ? Math.max(0, dataMaxValue - (Number.isNaN(cartQuantity) ? 0 : cartQuantity))
            : null;
        const maxValue = inputMaxValue !== null && !Number.isNaN(inputMaxValue) ? inputMaxValue : computedDataMax;

        let sanitizedValue = parsedValue;

        if (minValue !== null && !Number.isNaN(minValue)) {
          sanitizedValue = Math.max(sanitizedValue, minValue);
        }

        if (maxValue !== null && !Number.isNaN(maxValue)) {
          sanitizedValue = Math.min(sanitizedValue, maxValue);
        }

        if (sanitizedValue !== parsedValue) {
          this.quantityInput.value = sanitizedValue;
          this.quantityInput.setAttribute('value', sanitizedValue);
        }

        return sanitizedValue;
      }

      buildAllocation(requestedQuantity, currentAllocations = null) {
        if (!requestedQuantity || requestedQuantity < 0) {
          return { success: false, allocations: [], errorMessage: window.cartStrings.quantityError.replace('[quantity]', requestedQuantity) };
        }

        const variants = this.getQuickOrderVariants();
        if (!Array.isArray(variants)) {
          return { success: false, allocations: [], errorMessage: window.cartStrings.error };
        }

        const parsedVariants = variants.map((variant) => {
          const [rollPart, batchPart] = (variant.title || '').split('-');
          const batchDate = (batchPart || '').replace(/[^0-9]/g, '');
          const rollNumber = parseInt(rollPart, 10);
          const batchDateValue = this.getBatchDateValue(batchDate);
          const available = Math.max(0, parseInt(variant.inventory_quantity, 10) || 0);
          const searchableVariantText = `${variant.title || ''} ${variant.sku || ''}`.toLowerCase();

          if (
            variant.available === false ||
            available <= 0 ||
            !Number.isFinite(rollNumber) ||
            rollNumber <= 0 ||
            !batchDate ||
            !Number.isFinite(batchDateValue) ||
            batchDateValue <= 0 ||
            searchableVariantText.includes('pre-order') ||
            searchableVariantText.includes('sample') ||
            searchableVariantText.includes('swatch')
          ) {
            return null;
          }

          return {
            id: variant.id,
            title: variant.title,
            sku: variant.sku,
            rollNumber,
            batchDate,
            batchDateValue,
            available,
          };
        }).filter(Boolean);

        const maxSingleRollCapacity = parsedVariants.reduce((max, variant) => Math.max(max, variant.available), 0);

        const baseAllocations = [];

        if (currentAllocations) {
          parsedVariants.forEach((variant) => {
            const manualQuantity = Math.max(0, parseInt(currentAllocations[variant.id], 10) || 0);
            if (manualQuantity > 0) {
              const appliedQuantity = Math.min(manualQuantity, variant.available);
              variant.available = Math.max(0, variant.available - appliedQuantity);
              baseAllocations.push({ ...variant, quantity: appliedQuantity });
            }
          });
        }

        const baseTotal = baseAllocations.reduce((total, allocation) => total + allocation.quantity, 0);
        const remainingAvailable = parsedVariants.reduce((total, variant) => total + variant.available, 0);
        const effectiveTotalAvailable = remainingAvailable + baseTotal;

        if (requestedQuantity > effectiveTotalAvailable) {
          return {
            success: false,
            allocations: [],
            errorMessage: window.cartStrings.quantityError.replace('[quantity]', requestedQuantity),
          };
        }

        if (requestedQuantity <= baseTotal) {
          const baseOnlyAllocations = this.trimAllocations(baseAllocations, requestedQuantity);
          return {
            success: true,
            allocations: baseOnlyAllocations,
            messages: this.getAllocationMessages(baseOnlyAllocations, requestedQuantity, maxSingleRollCapacity),
          };
        }

        const additionalQuantityNeeded = requestedQuantity - baseTotal;
        const extraAllocations = this.allocateQuantityAcrossVariants(additionalQuantityNeeded, parsedVariants);

        if (!extraAllocations.success) {
          return extraAllocations;
        }

        const mergedAllocations = this.mergeAllocations(baseAllocations, extraAllocations.allocations);

        const allocationMessages = this.getAllocationMessages(
          mergedAllocations,
          requestedQuantity,
          maxSingleRollCapacity,
          extraAllocations.usesMultipleBatches
        );


        return { success: true, allocations: mergedAllocations, messages: allocationMessages };
      }

      getBatchDateValue(batchDate) {
        if (/^\d{8}$/.test(batchDate)) {
          const day = parseInt(batchDate.slice(0, 2), 10);
          const month = parseInt(batchDate.slice(2, 4), 10);
          const year = parseInt(batchDate.slice(4), 10);
          const parsedDate = new Date(Date.UTC(year, month - 1, day));

          if (
            parsedDate.getUTCFullYear() === year &&
            parsedDate.getUTCMonth() === month - 1 &&
            parsedDate.getUTCDate() === day
          ) {
            return year * 10000 + month * 100 + day;
          }
        }

        return parseInt(batchDate, 10);
      }

      allocateQuantityAcrossVariants(requestedQuantity, variants) {
        const batches = variants.reduce((map, variant) => {
          const existing = map.get(variant.batchDate) || { total: 0, variants: [], batchDateValue: variant.batchDateValue };
          existing.total += variant.available;
          existing.variants.push(variant);
          map.set(variant.batchDate, existing);
          return map;
        }, new Map());

        let allocations = [];
        let usesMultipleBatches = false;

        const singleRollCandidate = variants
          .filter((variant) => variant.available >= requestedQuantity)
          .sort((a, b) => {
            if (a.batchDateValue !== b.batchDateValue) {
              return a.batchDateValue - b.batchDateValue;
            }

            if (a.available !== b.available) {
              return a.available - b.available;
            }

            return a.rollNumber - b.rollNumber;
          })[0];

        if (singleRollCandidate) {
          allocations.push({ ...singleRollCandidate, quantity: requestedQuantity });
        } else {
          const sortedBatches = Array.from(batches.values()).sort((a, b) => a.batchDateValue - b.batchDateValue);
          let remaining = requestedQuantity;

          for (const batch of sortedBatches) {
            const sortedVariants = batch.variants.sort((a, b) => {
              if (a.available !== b.available) {
                return a.available - b.available;
              }

              return a.rollNumber - b.rollNumber;
            });

            for (const variant of sortedVariants) {
              if (remaining <= 0) break;
              const quantityTaken = Math.min(variant.available, remaining);
              if (quantityTaken > 0) {
                allocations.push({ ...variant, quantity: quantityTaken });
                remaining -= quantityTaken;
              }
            }

            if (remaining <= 0) break;
          }

          const batchesUsed = new Set(allocations.map((item) => item.batchDateValue));
          usesMultipleBatches = batchesUsed.size > 1;
        }

        return { success: true, allocations, usesMultipleBatches };
      }


      trimAllocations(baseAllocations, requestedQuantity) {
        const allocations = baseAllocations.map((allocation) => ({ ...allocation }));
        const total = allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
        let remainingToTrim = Math.max(0, total - requestedQuantity);

        const removalOrder = allocations.sort((a, b) => {
          if (a.quantity === b.quantity) {
            return a.batchDateValue - b.batchDateValue;
          }
          return a.quantity - b.quantity;
        });

        for (const allocation of removalOrder) {
          if (remainingToTrim <= 0) break;
          const amountToRemove = Math.min(allocation.quantity, remainingToTrim);
          allocation.quantity -= amountToRemove;
          remainingToTrim -= amountToRemove;
        }

        return allocations.filter((allocation) => allocation.quantity > 0);
      }

      mergeAllocations(existingAllocations, newAllocations) {
        const merged = existingAllocations.map((allocation) => ({ ...allocation }));

        newAllocations.forEach((allocation) => {
          const existing = merged.find((item) => item.id === allocation.id);
          if (existing) {
            existing.quantity += allocation.quantity;
          } else {
            merged.push({ ...allocation });
          }
        });

        return merged;
      }

      getAllocationMessages(allocations, requestedQuantity, maxSingleRollCapacity, usesMultipleBatches = false) {
        const messages = [];

        if (!Array.isArray(allocations) || allocations.length === 0) {
          return messages;
        }

        const needsMultipleRolls = typeof maxSingleRollCapacity === 'number' && requestedQuantity > maxSingleRollCapacity;
        const batchesUsed = new Set(allocations.map((item) => item.batchDateValue));
        const requiresMultipleBatches = usesMultipleBatches || batchesUsed.size > 1;

        if (needsMultipleRolls) {
          messages.push('multiple-rolls');
        }

        if (requiresMultipleBatches) {
          messages.push('multiple-batches');
        }

        return messages;
      }

      getAllocationMessage(key) {
        if (key === 'multiple-rolls') {
          return this.allocationMessageRolls?.textContent?.trim() || '';
        }

        if (key === 'multiple-batches') {
          return this.allocationMessageBatches?.textContent?.trim() || '';
        }

        return '';
      }

      updateAllocationMessages(messages = []) {
        if (!this.allocationMessageContainer) return;

        const messageSet = new Set(messages);
        const showRolls = Boolean(this.getAllocationMessage('multiple-rolls')) && messageSet.has('multiple-rolls');
        const showBatches =
          Boolean(this.getAllocationMessage('multiple-batches')) && messageSet.has('multiple-batches');

        this.allocationMessageContainer.hidden = !(showRolls || showBatches);

        if (this.allocationMessageRolls) {
          this.allocationMessageRolls.hidden = !showRolls;
        }

        if (this.allocationMessageBatches) {
          this.allocationMessageBatches.hidden = !showBatches;
        }
      }

      applyAllocationToTable(allocations, messages = []) {
        if (!allocations || !this.quickOrderList) return;

        const allocationMap = allocations.reduce((map, allocation) => {
          map[allocation.id] = allocation.quantity;
          return map;
        }, {});

        this.updateAllocationMessages(messages);

        const allocationMessageText = Array.isArray(messages)
          ? messages
              .map((messageKey) => this.getAllocationMessage(messageKey))
              .filter(Boolean)
              .join(' ')
          : '';

        if (typeof this.quickOrderList.applyAllocation === 'function') {
          this.quickOrderList.applyAllocation(allocationMap, allocationMessageText);
        }
      }

      addAllocatedItemsToCart(allocations) {
        const config = fetchConfig('javascript');
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({
          items: allocations.map((allocation) => ({ id: allocation.id, quantity: allocation.quantity })),
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, config)
          .then(parseCartMutationResponse)
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings.error;
              this.handleErrorMessage(message);
              setCartMutationError(message);
              return;
            }

            const refreshPromise =
              typeof this.quickOrderList?.refresh === 'function' ? this.quickOrderList.refresh() : Promise.resolve();

            Promise.resolve(refreshPromise).then(() => {
              if (typeof this.quickOrderList?.resetQuantityInputs === 'function') {
                this.quickOrderList.resetQuantityInputs('cart-add', true, false);
              }
            });

            renderCartAddResponse(this.cart, response, this.variantIdInput?.value, 'product-form');
          })
          .catch((error) => {
            console.error('[ProductForm] Failed to add allocated items to cart', error);
            const message = error instanceof Error ? error.message : window.cartStrings.error;
            this.handleErrorMessage(message);
            setCartMutationError(message);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButtonSpinner?.classList.add('hidden');
          });
      }

      addSingleItemToCart(evt) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButtonSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(parseCartMutationResponse)
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings.error;
              this.handleErrorMessage(message);
              setCartMutationError(message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              return;
            }

            renderCartAddResponse(this.cart, response, this.variantIdInput?.value, 'product-form');
          })
          .catch((error) => {
            console.error('[ProductForm] Failed to add item to cart', error);
            const message = error instanceof Error ? error.message : window.cartStrings.error;
            this.handleErrorMessage(message);
            setCartMutationError(message);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButtonSpinner?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      setSingleQuantityMax() {
        this.refreshQuantityElements();
        if (!this.quantityInput) return null;

        const totalRemaining = this.getTotalRemainingInventory();

        if (typeof totalRemaining === 'number' && !Number.isNaN(totalRemaining)) {
          const safeTotal = Math.max(0, totalRemaining);
          this.quantityInput.setAttribute('max', safeTotal);
          this.quantityInput.setAttribute('data-max', safeTotal);
          return safeTotal;
        }

        this.quantityInput.removeAttribute('max');
        this.quantityInput.removeAttribute('data-max');
        return null;
      }

      parseQuickOrderVariantData() {
        const variantData = this.quickOrderList?.dataset?.variantData || '[]';

        try {
          const parsedVariants = JSON.parse(variantData);
          return Array.isArray(parsedVariants) ? parsedVariants : [];
        } catch (error) {
          return null;
        }
      }

      getQuickOrderVariants() {
        const variants = this.parseQuickOrderVariantData();
        if (!Array.isArray(variants)) return null;

        return variants.map((variant) => ({
          ...variant,
          inventory_quantity: Math.max(0, parseInt(variant.inventory_quantity, 10) || 0),
        }));
      }

      getTotalRemainingInventory() {
        const variants = this.getQuickOrderVariants();
        if (!Array.isArray(variants)) return null;

        return variants.reduce((total, variant) => total + variant.inventory_quantity, 0);
      }

      applySingleQuantityAllocation() {
        this.refreshQuantityElements();
        if (!this.shouldAutoAllocate() || this.quickOrderList?.isManualMode) return;

        const isSingleQuantity = this.quantityInput?.dataset?.singleQuantity === 'true';
        if (!isSingleQuantity) return;

        const maxAvailable = this.setSingleQuantityMax();
        const minimumQuantity = this.quantityInput?.dataset?.min
          ? parseInt(this.quantityInput.dataset.min, 10)
          : parseInt(this.quantityInput?.min, 10);
        const safeDefaultQuantity =
          Number.isFinite(minimumQuantity) && minimumQuantity > 0 ? minimumQuantity : 1;
        const defaultQuantity =
          typeof maxAvailable === 'number' ? Math.min(safeDefaultQuantity, maxAvailable) : safeDefaultQuantity;

        this.quantityInput.value = defaultQuantity;
        this.quantityInput.setAttribute('value', defaultQuantity);

        const allocation = this.buildAllocation(defaultQuantity, this.getCurrentAllocationsIfManual());

        if (!allocation.success) {
          if (typeof this.quickOrderList?.applyAllocation === 'function') {
            this.quickOrderList.applyAllocation({}, '');
          }
          return;
        }

        this.applyAllocationToTable(allocation.allocations, allocation.messages);
      }

      getCurrentAllocationsIfManual() {
        if (!this.quickOrderList?.isManualMode) return null;

        return typeof this.quickOrderList.getCurrentAllocations === 'function'
          ? this.quickOrderList.getCurrentAllocations()
          : null;
      }

      getManualSelectionMessages() {
        const currentAllocations = this.getCurrentAllocationsIfManual();
        if (!currentAllocations) return [];

        const selectedVariantIds = Object.entries(currentAllocations)
          .filter(([, quantity]) => (parseInt(quantity, 10) || 0) > 0)
          .map(([variantId]) => String(variantId));

        if (selectedVariantIds.length === 0) return [];

        const messages = [];
        if (selectedVariantIds.length > 1) {
          messages.push('multiple-rolls');
        }

        const variantMap = this.getQuickOrderVariantMap();
        const selectedBatchKeys = new Set(
          selectedVariantIds
            .map((variantId) => variantMap[variantId]?.batchDateValue)
            .filter((batchDateValue) => Number.isFinite(batchDateValue))
        );

        if (selectedBatchKeys.size > 1) {
          messages.push('multiple-batches');
        }

        return messages;
      }

      getQuickOrderVariantMap() {
        const variants = this.getQuickOrderVariants();
        if (!Array.isArray(variants)) {
          return {};
        }

        return variants.reduce((map, variant) => {
          const [, batchPart] = (variant.title || '').split('-');
          const batchDate = (batchPart || '').replace(/[^0-9]/g, '');
          map[String(variant.id)] = {
            batchDateValue: this.getBatchDateValue(batchDate),
          };
          return map;
        }, {});
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get submitButtonSpinner() {
        return this.submitButton?.querySelector('.loading__spinner');
      }
    }
  );
}

const cartNotificationComponent = () =>
  document.querySelector('cart-notification') || document.querySelector('cart-drawer');

const parseCartMutationResponse = async (response) => {
  const responseText = await response.text();
  let parsedResponse = null;

  try {
    parsedResponse = responseText ? JSON.parse(responseText) : null;
  } catch (error) {
    throw new Error(window.cartStrings.error);
  }

  if (!response.ok || !parsedResponse) {
    const message = parsedResponse?.description || parsedResponse?.message || window.cartStrings.error;
    throw new Error(message);
  }

  return parsedResponse;
};

const setCartMutationError = (message) => {
  const errorContainer = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
  if (!errorContainer) return;
  errorContainer.textContent = message || window.cartStrings.error;
};

const renderCartAddResponse = (cart, response, variantId, source) => {
  const publishCartUpdate = () => {
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source,
      cartData: response,
      variantId,
    });
  };

  if (cart) {
    const canRenderSections = response?.sections && typeof response.sections === 'object';

    if (canRenderSections) {
      try {
        cart.renderContents(response);
        publishCartUpdate();
        return;
      } catch (error) {
        console.error('[ProductForm] Failed to render cart sections after add-to-cart', error);
      }
    }

    if (typeof cart.onCartUpdate === 'function') {
      cart.onCartUpdate();
      publishCartUpdate();
      return;
    }
  }

  window.location = window.routes.cart_url;
};

const resetUpsellButtonState = (button) => {
  button.classList.remove('loading');
  button.removeAttribute('aria-disabled');
  button.querySelector('.loading__spinner')?.classList.add('hidden');
};

const handleUpsellAddToCart = (event) => {
  const button = event.target.closest('.product-upsells__add');
  if (!button || button.classList.contains('loading')) return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const cart = cartNotificationComponent();
  const sections = cart?.getSectionsToRender?.().map((section) => section.id);

  button.setAttribute('aria-disabled', 'true');
  button.classList.add('loading');
  button.querySelector('.loading__spinner')?.classList.remove('hidden');

  const config = fetchConfig('json');
  config.headers['Content-Type'] = 'application/json';
  config.body = JSON.stringify({
    items: [{ id: variantId, quantity: 1 }],
    sections,
    sections_url: window.location.pathname,
  });

  cart?.setActiveElement?.(document.activeElement);

  fetch(`${routes.cart_add_url}`, config)
    .then(parseCartMutationResponse)
    .then((response) => {
      if (response.status) {
        setCartMutationError(response.description || response.message || window.cartStrings.error);
        resetUpsellButtonState(button);
        return;
      }

      renderCartAddResponse(cartNotificationComponent(), response, variantId, 'product-upsell');
    })
    .catch((error) => {
      console.error('[ProductForm] Failed to add upsell item to cart', error);
      const message = error instanceof Error ? error.message : window.cartStrings.error;
      setCartMutationError(message);
      resetUpsellButtonState(button);
    })
    .finally(() => {
      resetUpsellButtonState(button);
    });
};

document.addEventListener('click', handleUpsellAddToCart);
